import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: API_URL, headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use(config => {
  let token = localStorage.getItem('token');
  if (!token && typeof sessionStorage !== 'undefined') {
    token = sessionStorage.getItem('token');
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    // Sadece 401 (token süresi doldu) durumunda logout yap, 403 (permission denied) değil
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(err);
  }
);

export const authService = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  },
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
};

export const animalService = {
  list: (farmId, page = 1, limit = 50, filters = {}) =>
    api.get('/animals', { params: { farm_id: farmId, page, limit, ...filters } }),
  get: (id, farmId) => api.get(`/animals/${id}`, { params: { farm_id: farmId } }),
  create: (farmId, data) => api.post('/animals', { farm_id: farmId, ...data }),
  update: (id, farmId, data) => api.put(`/animals/${id}`, { farm_id: farmId, ...data }),
  delete: (id, farmId, reason = 'dead') =>
    api.delete(`/animals/${id}`, { params: { farm_id: farmId, reason } }),
  batchImport: (farmId, animals) => api.post('/animals/batch-import', { farm_id: farmId, animals }),
  stats: (farmId) => api.get('/animals/stats', { params: { farm_id: farmId } }),
};

export const healthService = {
  list: (farmId, filters = {}) =>
    api.get('/health', { params: { farm_id: farmId, ...filters } }),
  get: (id, farmId) => api.get(`/health/${id}`, { params: { farm_id: farmId } }),
  create: (farmId, data) => api.post('/health', { farm_id: farmId, ...data }),
  update: (id, farmId, data) => api.put(`/health/${id}`, { farm_id: farmId, ...data }),
  delete: (id, farmId) => api.delete(`/health/${id}`, { params: { farm_id: farmId } }),
  upcomingVaccinations: (farmId, days = 30) =>
    api.get('/health/upcoming/vaccinations', { params: { farm_id: farmId, days } }),
};

export const milkService = {
  list: (farmId, filters = {}) =>
    api.get('/milk', { params: { farm_id: farmId, ...filters } }),
  summary: (farmId, month) =>
    api.get('/milk/summary', { params: { farm_id: farmId, month } }),
  create: (farmId, data) => api.post('/milk', { farm_id: farmId, ...data }),
  update: (id, farmId, data) => api.put(`/milk/${id}`, { farm_id: farmId, ...data }),
  delete: (id, farmId) => api.delete(`/milk/${id}`, { params: { farm_id: farmId } }),
};

export const financeService = {
  list: (farmId, filters = {}) =>
    api.get('/finance', { params: { farm_id: farmId, ...filters } }),
  summary: (farmId, month) =>
    api.get('/finance/summary', { params: { farm_id: farmId, month } }),
  create: (farmId, data) => api.post('/finance', { farm_id: farmId, ...data }),
  update: (id, farmId, data) => api.put(`/finance/${id}`, { farm_id: farmId, ...data }),
  delete: (id, farmId) => api.delete(`/finance/${id}`, { params: { farm_id: farmId } }),
};

export const tasksService = {
  list: (farmId, filters = {}) =>
    api.get('/tasks', { params: { farm_id: farmId, ...filters } }),
  upcoming: (farmId, params = {}) =>
    api.get('/tasks/upcoming', { params: { farm_id: farmId, ...params } }),
  create: (farmId, data) => api.post('/tasks', { farm_id: farmId, ...data }),
  complete: (farmId, id, notes) =>
    api.put(`/tasks/${id}/complete`, { farm_id: farmId, notes }),
  update: (id, farmId, data) => api.put(`/tasks/${id}`, { farm_id: farmId, ...data }),
  delete: (id, farmId) => api.delete(`/tasks/${id}`, { params: { farm_id: farmId } }),
};

export const veterinaryService = {
  listReports: (farmId, filters = {}) =>
    api.get('/veterinary/reports', { params: { farm_id: farmId, ...filters } }),
  getReport: (id, farmId) =>
    api.get(`/veterinary/reports/${id}`, { params: { farm_id: farmId } }),
  createReport: (farmId, data) =>
    api.post('/veterinary/reports', { farm_id: farmId, ...data }),
  signReport: (id, farmId) =>
    api.put(`/veterinary/reports/${id}/sign`, { farm_id: farmId }),
  acknowledgeReport: (id, farmId) =>
    api.post(`/veterinary/reports/${id}/acknowledge`, { farm_id: farmId }),
};

export const reproductionService = {
  list: (farmId, filters = {}) =>
    api.get('/reproduction', { params: { farm_id: farmId, ...filters } }),
  get: (id, farmId) =>
    api.get(`/reproduction/${id}`, { params: { farm_id: farmId } }),
  create: (farmId, data) =>
    api.post('/reproduction', { farm_id: farmId, ...data }),
  update: (id, farmId, data) =>
    api.put(`/reproduction/${id}`, { farm_id: farmId, ...data }),
  delete: (id, farmId) =>
    api.delete(`/reproduction/${id}`, { params: { farm_id: farmId } }),
};

export const reportsService = {
  generate: (farmId, params) =>
    api.get('/reports/generate', { params: { farm_id: farmId, ...params } }),
  generatePDF: (farmId, params) =>
    api.get('/reports/pdf', {
      params: { farm_id: farmId, ...params },
      responseType: 'arraybuffer'
    }),
};

export const inventoryService = {
  list: (farmId, filters = {}) =>
    api.get('/inventory', { params: { farm_id: farmId, ...filters } }),
  get: (id, farmId) =>
    api.get(`/inventory/${id}`, { params: { farm_id: farmId } }),
  create: (farmId, data) =>
    api.post('/inventory', { farm_id: farmId, ...data }),
  update: (farmId, id, data) =>
    api.put(`/inventory/${id}`, { farm_id: farmId, ...data }),
  delete: (farmId, id) =>
    api.delete(`/inventory/${id}`, { params: { farm_id: farmId } }),
};

export default api;